import { createSlice, PayloadAction } from "@reduxjs/toolkit";

// Define types for source and destination fields
interface SourceDetails {
    id: string;
    source_account_id: string;
    source_account_name: string;
    purchased_date: string;
    purchased_amount: string;
    
    receipt_url: File | null;
    notes: string;
}
interface DestinationDetails {
    id: string;
    destination_account_id: number;
    destination_account_name: string;
    purchased_date: string;
    purchased_amount: string;
    pending_amount: string;
    receipt_url: File | null;
    notes: string;
}

// Define the state type
interface TransactionState {
    mode: "Paid" | "Advance"; // Can be "paid", "advance", or an empty string initially
    credit_status: boolean;
    sources: SourceDetails[];
    destinations: DestinationDetails[];
}

// Initial state
const initialState: TransactionState = {
    mode: "Paid",
    credit_status: false,
    sources: [{
        id: "initial",
        source_account_id: "",
        source_account_name: "",
        purchased_date: "",
        purchased_amount: "",
        receipt_url: null,
        notes: "",
    }],
    destinations: [{
        id: "initial",
        destination_account_id: 1,
        destination_account_name: "",
        purchased_date: "",
        purchased_amount: "",
        pending_amount: "",
        receipt_url: null,
        notes: "",
    }],
};

// Create slice
const transactionSlice = createSlice({
    name: "transaction",
    initialState,
    reducers: {
        setMode: (state, action: PayloadAction<"Paid" | "Advance">) => {
            state.mode = action.payload;
            if (action.payload === "Advance") {
                state.destinations = [initialState.destinations[0]]; // Reset destinations for "advance"
            }
        },
        setCreditStatus: (state, action: PayloadAction<boolean>) => {
            state.credit_status = action.payload;
        },
        setSourceField: (state, action: PayloadAction<{ source_account_id: number; id: string; value: string | boolean }>) => {
            const sourceIndex: number = action.payload.source_account_id;
            if (state.sources[sourceIndex]) {
                state.sources[sourceIndex][action.payload.id] = action.payload.value;
            }
        },        
        setDestinationField: (state, action: PayloadAction<{ destination_account_id: number; id: string; value: string | boolean }>) => {
            const destinationIndex: number = action.payload.destination_account_id;
            if (state.destinations[destinationIndex]) {
                state.destinations[destinationIndex][action.payload.id] = action.payload.value;
            }
        },        addSource: (state) => {
            if (state.sources.length < 20) {
                const uniqueId = `src_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
                state.sources.push({
                    ...initialState.sources[0],
                    id: uniqueId,
                });
            }
        },
        addDestination: (state) => {
            const uniqueId = `dst_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
            state.destinations.push({
                ...initialState.destinations[0],
                id: uniqueId,
            });
        },
        removeSource: (state, action: PayloadAction<string>) => {
            state.sources = state.sources.filter(source => source.id !== action.payload);
        },
        removeDestination: (state, action: PayloadAction<string>) => {
            state.destinations = state.destinations.filter(destination => destination.id !== action.payload);
        },
        resetManualState: () => initialState,
    },
});

// Export actions and reducer
export const { 
    setMode, 
    setCreditStatus,
    setSourceField, 
    setDestinationField, 
    addSource, 
    addDestination,
    removeSource, 
    removeDestination,
    resetManualState 
} = transactionSlice.actions;
export default transactionSlice.reducer;